import React, { useMemo, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';

type RadioGroupChildrenProps = {
	name: string;
	onChange: (value: string) => void;
	selectedValue: string;
};

type RadioGroupProps = {
	children: (props: RadioGroupChildrenProps) => React.ReactNode;
	name: string;
	onChange?: (value: string) => void;
	value: string;
};

const radioGroupStyles = cssMap({
	container: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
	},
});

export const RadioGroup = ({
	children,
	onChange,
	value,
	name,
}: RadioGroupProps): React.JSX.Element => {
	const [selectedValue, setSelectedValue] = useState(value);
	const isControlled = onChange !== undefined;

	const props = useMemo(
		() => ({
			name,
			selectedValue: isControlled ? value : selectedValue,
			onChange: (value: string) => {
				isControlled ? onChange(value) : setSelectedValue(value);
			},
		}),
		[name, value, selectedValue, isControlled, onChange],
	);

	return <Stack xcss={radioGroupStyles.container}>{children(props)}</Stack>;
};

type RadioButtonProps = {
	actions?: React.ReactNode;
	children?: React.ReactNode;
	description?: string;
	isDisabled?: boolean;
	label: string;
	labelAfter?: React.ReactNode;
	lastChild?: boolean;
	name: string;
	testId?: string;
	value: string;
} & RadioGroupChildrenProps;

const radioButtonStyles = cssMap({
	container: {
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	outerWrapper: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	innerWrapper: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
	},
	lastChild: {
		borderBottom: 'none',
	},
});

export const RadioButton = ({
	label,
	description,
	actions,
	labelAfter,
	name,
	value,
	children,
	isDisabled,
	selectedValue,
	lastChild,
	onChange,
	testId,
}: RadioButtonProps): React.JSX.Element => {
	return (
		<Box xcss={cx(radioButtonStyles.container, lastChild && radioButtonStyles.lastChild)}>
			<Inline spread="space-between" alignBlock="center" xcss={radioButtonStyles.outerWrapper}>
				<Inline xcss={radioButtonStyles.innerWrapper} space="space.100" alignBlock="center">
					<Radio
						value={value}
						onChange={() => onChange(value)}
						isChecked={value === selectedValue}
						isDisabled={isDisabled}
						name={name}
						testId={testId}
						ariaLabel={label}
					/>
					<Stack>
						<Inline space="space.050" alignBlock="center">
							<Text>{label}</Text>
							{labelAfter}
						</Inline>
						{description && <Text color="color.text.subtlest">{description}</Text>}
					</Stack>
				</Inline>
				{actions && <Box>{actions}</Box>}
			</Inline>
			{children}
		</Box>
	);
};
